<template>
    <v-card>
        <v-card-title>
            <v-dialog v-model="dialog" max-width="800px">
                <template v-slot:activator="{ on }">
                    <v-btn color="primary" dark class="mb-2" v-on="on">{{ trans('buttons.domain.newItem') }}</v-btn>
                </template>
                <template v-if="editedItem">
                    <domain-form v-if="dialogLoaded" :item-id="editedItem" v-on:saved="onSave" v-on:updated="onSave" v-on:close="close"></domain-form>
                </template>
                <template v-else>
                    <template v-if="this.options && this.options.vendorDomains && this.options.vendorDomains.length">
                        <v-tabs
                                v-if="$store.getters.user.userCpaIntegrations[$store.getters.vendorId]"
                                background-color="primary"
                                dark
                        >
                            <v-tab>
                                {{trans('pages.domain.vendorSubDomain')}}
                            </v-tab>
                            <v-tab>
                                {{trans('pages.domain.ownDomain')}}
                            </v-tab>
                            <v-tab-item>
                                <domain-vendor-form v-if="dialogLoaded" v-on:saved="onSave" v-on:updated="onSave" v-on:close="close"></domain-vendor-form>
                            </v-tab-item>
                            <v-tab-item>
                                <domain-form v-if="dialogLoaded" v-on:saved="onSave" v-on:updated="onSave" v-on:close="close"></domain-form>
                            </v-tab-item>
                        </v-tabs>
                    </template>
                    <template v-else>
                        <domain-form v-if="dialogLoaded" :item-id="editedItem" v-on:saved="onSave" v-on:updated="onSave" v-on:close="close"></domain-form>
                    </template>
                </template>
            </v-dialog>
            <v-dialog v-model="showItemDialog" max-width="800px">
                <domain-card v-if="showItemDialogLoaded" :id="showItemId" :key="showItemId" v-on:close="showItemDialogClose" v-on:click:outside="showItemDialogClose"></domain-card>
            </v-dialog>
            <delete-dialog
                :dialogTrigger="deleteDialog"
                :item-id="deleteItemId"
                :delete-route="deleteRoute"
                @closed="deleteDialogClosed"
                @deleted="deleteDialogDeleted"
            >
                <template v-slot:title>
                    {{ trans('pages.domain.deleteDialogTitle') }}
                </template>
                <template v-slot:text>
                    {{ trans('pages.domain.deleteDialogText', {id: deleteItemText ? '"'+deleteItemText+'"' : deleteItemId}) }}
                </template>
            </delete-dialog>
            <v-spacer></v-spacer>
            <v-icon
                class="pull-right"
                color="primary"
                @click="filterShow = filterShow === 0 ? null : 0"
            >mdi-filter</v-icon>
            <v-expansion-panels
                v-model="filterShow"
                flat
                tile
            >
                <v-expansion-panel>
                    <v-expansion-panel-content class="pt-4">
                        <v-row>
                            <v-col style="min-width: 200px;">
                                <v-text-field
                                    v-model="filters.name"
                                    :label="trans('fields.common.domainName')"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-btn
                                class="ma-2"
                                color="primary"
                                outlined
                                @click="applyFilters"
                            >{{ trans('buttons.common.apply') }}</v-btn>
                            <v-btn
                                class="ma-2"
                                color="orange"
                                outlined
                                @click="clearFilters"
                            >{{ trans('buttons.common.clear') }}</v-btn>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-card-title>
        <v-data-table
            :headers="headers"
            :items="domains.data"
            :items-per-page="paginationParams.per_page"
            :page="paginationParams.page"
            :server-items-length="domains.total"
            :loading="loading"
            :footer-props="footerProps"
            sort-by="id"
            class="border"
            @update:page="changePage"
            @update:items-per-page="changeItemsPerPage"
        >
            <template v-slot:top>
            </template>
            <template v-slot:item.actions="{ item }">
                <v-icon small class="mr-2" @click="showItem(item)">mdi-eye</v-icon>
                <v-icon small class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
                <v-icon small @click="deleteDialogShow(item.id, item.name)">mdi-delete</v-icon>
            </template>
            <template v-slot:item.created_at="{ item }">
                {{ item.created_at | mysqlDate }}
            </template>
            <template v-slot:item.last_check="{ item }">
                {{ item.last_check | mysqlDate }}
            </template>
            <template v-slot:item.nextCheck="{ item }">
                {{ item.nextCheck | mysqlDate }}
            </template>
            <template v-slot:item.user="{ item }">
                <template v-if="item.user">{{ item.user.id }} :: {{ item.user.email }}</template>
            </template>
            <template v-slot:item.landing="{ item }">
                <template v-if="item.landing">{{ item.landing.label }}</template>
            </template>
            <template v-slot:item.status="{ item }">
                <v-chip
                    v-if="item.enabled"
                    class="ma-2"
                    :color="item.statusColor"
                    text-color="white"
                >
                    <v-avatar left>
                        <v-icon>{{ item.statusIcon }}</v-icon>
                    </v-avatar>
                    {{ item.statusLabel }}
                </v-chip>
                <v-chip
                    v-else
                    class="ma-2"
                    color="red"
                    text-color="white"
                >
                    <v-avatar left>
                        <v-icon>mdi mdi-reload-alert</v-icon>
                    </v-avatar>
                    {{ trans('domain.status.disabled') }}
                </v-chip>
                <p v-if="item.error_message">{{ trans('domain.error_messages.'+item.error_message) }}</p>
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
    import tableWithPagination from "@/mixins/tableWithPagination";
    import DomainCard from "./DomainCard";
    import DeleteDialog from "@/components/commonComponents/DeleteDialog";
    import deleteDialog from "@/mixins/deleteDialog";
    import tableFilters from "@/mixins/tableFilters";

    export default {
        name: "DomainTable",
        components: {
            DomainCard,
            DomainForm: () => import('@/components/user/domain/DomainForm'),
            DomainVendorForm: () => import('@/components/user/domain/DomainVendorForm'),
            DeleteDialog,
        },
        mixins: [tableWithPagination, deleteDialog, tableFilters],
        data() {
            return {
                dialog: false,
                dialogLoaded: false,
                showItemDialog: false,
                showItemDialogLoaded: false,
                domains: {},
                headers: [
                    { text: "id", sortable: false, value: "id" },
                    { text: this.trans("fields.common.domainName"), value: "name", sortable: false },
                    { text: this.trans("fields.common.dns_a"), value: "dns_a", sortable: false },
                    { text: this.trans("fields.common.dns_last_check"), value: "last_check", sortable: false },
                    { text: this.trans("fields.common.dns_next_check"), value: "nextCheck", sortable: false },
                    { text: this.trans("fields.common.status"), value: "status", sortable: false },
                    { text: this.trans("fields.common.landing"), value: "landing", sortable: false },
                    { text: this.trans("fields.common.created_at_m"), value: "created_at", sortable: false },
                    { text: this.trans("fields.common.actions"), value: "actions", sortable: false },
                ],
                paginationParams: {
                    page: 1,
                    per_page: 10,
                },
                url: this.route('user.domains.index'),
                deleteRoute: 'user.domains.delete',

                filters: {
                    name: '',
                },
                filtersDefault: {
                    name: '',
                },


                showItemId: null,
                editedItem: null,
                options: {},
            }
        },
        created() {
            this.editedItem = null;
            // this.initialize();
            // this.loadOptions();
            // setInterval( () => {
            //     if (!this.loading){
            //         this.initialize();
            //     }
            // }, 30000);
            this.loadOptions();
        },
        watch: {
            dialog(val) {
                if (val){
                    this.dialogLoaded = true
                } else {
                    this.close();
                }
            }
        },
        methods: {
            onSave(){
                this.close();
                this.initialize();
            },
            deleted() {
                this.initialize()
            },

            showItem(item) {
                this.showItemId = item.id;
                this.showItemDialog = true;
                this.showItemDialogLoaded = true;
            },
            editItem(item) {
                this.editedItem = item.id;
                this.dialog = true;
                this.dialogLoaded = true;
            },

            close() {
                this.dialog = false;
                setTimeout(() => {
                    this.editedItem = null;
                    this.dialogLoaded = false;
                }, 300);
            },
            showItemDialogClose() {
                this.showItemDialog = false;
                setTimeout(() => {
                    this.showItemId = null;
                    this.showItemDialogLoaded = false;
                }, 300);
            },
            loadOptions(){
                axios.post(this.route('user.options'), {
                    vendorDomains: true,
                }).then( response => {
                    this.options = response.data;
                })
            },


        },
        computed: {
            paginationItems: {
                get(){
                    return this.domains;
                },
                set(v){
                    this.domains = v;
                }
            },
        }
    }
</script>

<style scoped>

</style>
